import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { formatDate } from 'lib/utils';
import { useSavedCards } from 'features/credits/hooks/use-credits';
import { cardBrand, usableCards } from 'features/credits/utils/cards';
import { BuySeatsDialog } from 'features/iam/components/seats/buy-seats-dialog';
import { seatsLabel } from 'features/iam/components/seats/plan-actions';
import { formatMinor, PlanSeats, seatPriceLabel } from '../utils/seat-pools';
import { SeatsInUse } from './seats-in-use';

interface NextBillCardProps {
  planSeats: PlanSeats[];
}

/** What the organization pays at the next renewal, plan by plan, and how many seats are in use. */
export const NextBillCard = ({ planSeats }: NextBillCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isManaging, setIsManaging] = useState(false);
  const { data: cards } = useSavedCards();

  // An unpaid plan has no seats yet, and a cancelled one ends with the period instead of renewing.
  const live = planSeats.filter((entry) => entry.pool && !entry.awaitingPayment);
  const renewing = live.filter((entry) => !entry.cancelScheduled && entry.unitAmountMinor);

  const currency = renewing[0]?.currencyCode ?? 'CHF';
  // The "/ month" suffix only holds when every plan renews on the same cycle.
  const interval = renewing.every((entry) => entry.interval === renewing[0].interval)
    ? renewing[0]?.interval?.toLowerCase()
    : undefined;
  const intervalLabel = interval ? t(`INTERVAL_${interval.toUpperCase()}`, interval) : '';
  const lineTotal = (entry: PlanSeats) => entry.seatsNextPeriod * (entry.unitAmountMinor ?? 0);
  const totalMinor = renewing.reduce((sum, entry) => sum + lineTotal(entry), 0);

  const nextChargeTimes = renewing
    .map((entry) => entry.pool?.subscription.nextPaymentAtUtc ?? entry.periodEndUtc)
    .filter((date): date is string => !!date)
    .map((date) => new Date(date).getTime());
  const nextChargeDate = nextChargeTimes.length ? formatDate(new Date(Math.min(...nextChargeTimes)).toISOString()) : '';
  const card = usableCards(cards)[0];

  const held = live.reduce((sum, entry) => sum + entry.held, 0);
  const totalSeats = live.reduce((sum, entry) => sum + entry.held + entry.freeThisPeriod, 0);
  const unusedSeats = renewing.reduce((sum, entry) => sum + Math.max(0, entry.seatsNextPeriod - entry.held), 0);
  const unusedMinor = renewing.reduce(
    (sum, entry) => sum + Math.max(0, entry.seatsNextPeriod - entry.held) * (entry.unitAmountMinor ?? 0),
    0
  );

  let chargedOn = '';
  if (nextChargeDate && card?.lastFour) {
    chargedOn = t('NEXT_BILL_CHARGED_ON_CARD', {
      date: nextChargeDate,
      card: cardBrand(card, t('CARD', 'Card')),
      lastFour: card.lastFour,
      defaultValue: 'Charged on {{date}} to {{card}} ending {{lastFour}}',
    });
  } else if (nextChargeDate) {
    chargedOn = t('NEXT_BILL_CHARGED_ON', { date: nextChargeDate, defaultValue: 'Charged on {{date}}' });
  }

  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none lg:col-span-3">
      <CardContent className="flex h-full flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-sm font-medium text-foreground">{t('NEXT_BILL', 'Next bill')}</h2>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setIsManaging(true)}>
            {t('MANAGE_SEATS', 'Manage seats')}
          </Button>
        </div>

        {renewing.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('NEXT_BILL_EMPTY', 'Nothing renews yet. Buy seats to start a plan.')}
          </p>
        ) : (
          <div>
            <p className="flex flex-wrap items-baseline gap-2">
              <span className="text-2xl font-medium leading-heading text-foreground sm:text-3xl sm:leading-heading">
                {formatMinor(totalMinor, currency)}
              </span>
              {intervalLabel && <span className="text-sm text-muted-foreground">/ {intervalLabel}</span>}
            </p>
            {chargedOn && <p className="mt-1 text-sm text-muted-foreground">{chargedOn}</p>}
          </div>
        )}

        {renewing.length > 0 && (
          <ul className="flex flex-col gap-2 border-t border-border pt-4 text-sm">
            {renewing.map((entry) => (
              <li key={entry.code} className="flex items-start justify-between gap-3">
                <span className="min-w-0 text-muted-foreground">
                  {`${entry.name} · ${seatsLabel(t, entry.seatsNextPeriod)} × ${seatPriceLabel(t, entry)}`}
                </span>
                <span className="shrink-0 text-foreground">{formatMinor(lineTotal(entry), entry.currencyCode)}</span>
              </li>
            ))}
          </ul>
        )}

        {totalSeats > 0 && (
          <div className="mt-auto flex flex-col gap-2 border-t border-border pt-4 text-sm">
            <SeatsInUse held={held} total={totalSeats} />
            {unusedSeats > 0 && intervalLabel && (
              <p className="text-muted-foreground">
                {unusedSeats === 1
                  ? t('UNUSED_SEAT_COST_ONE', {
                      price: formatMinor(unusedMinor, currency),
                      interval: intervalLabel,
                      defaultValue: '1 unused seat costs you {{price}} a {{interval}}.',
                    })
                  : t('UNUSED_SEATS_COST', {
                      count: unusedSeats,
                      price: formatMinor(unusedMinor, currency),
                      interval: intervalLabel,
                      defaultValue: '{{count}} unused seats cost you {{price}} a {{interval}}.',
                    })}{' '}
                <button
                  type="button"
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                  onClick={() => navigate('/identity-management')}
                >
                  {t('GIVE_TO_SOMEONE', 'Give to someone')}
                </button>
              </p>
            )}
          </div>
        )}
      </CardContent>

      {/* Every plan is offered; a single plan on the bill opens already chosen. */}
      <BuySeatsDialog
        open={isManaging}
        planSeats={planSeats}
        focusCode={renewing.length === 1 ? renewing[0].code : undefined}
        onClose={() => setIsManaging(false)}
      />
    </Card>
  );
};
